import { useEffect, useRef, useState, useCallback } from 'react'
import { CreditCard, CheckCircle, Loader2 } from 'lucide-react'
import type { QuestionProps } from '../eform.types'
import { eformService } from '../eform.service'

type ForwardPaymentElement = {
  mount: (el: HTMLElement, options: Record<string, unknown>) => void
  unmount?: () => void
  destroy?: () => void
}

type ForwardWindow = Window & {
  Forward?: {
    createPaymentElement: (config: {
      apiKey: string
      clientSecret: string
    }) => Promise<ForwardPaymentElement>
  }
}

interface PaymentFieldProps extends QuestionProps {
  eformToken?: string
}

export default function PaymentField({ question,  onChange, error, eformToken }: PaymentFieldProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const paymentElementRef = useRef<ForwardPaymentElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [cardAdded, setCardAdded] = useState(false)
  const [cardTitle, setCardTitle] = useState('')
  const mountedRef = useRef(false)

  const merchantId = parseInt(question.instructionText || '0', 10)

  const handleSuccess = useCallback(
    (token: string, title: string, expireDate: string, cardType: string) => {
      const pipeValue = `${token}|${merchantId}|true|${title}|${expireDate}|${cardType}`
      onChange(pipeValue)
      setCardAdded(true)
      setCardTitle(title)
    },
    [merchantId, onChange],
  )

  useEffect(() => {
    if (!eformToken || merchantId <= 0 || !containerRef.current || mountedRef.current) return

    let cancelled = false
    setLoading(true)
    setFieldError(null)

    async function init() {
      try {
        const intent = await eformService.createPaymentIntent(eformToken!, merchantId)

        if (cancelled) return

        const forwardWindow = window as unknown as ForwardWindow
        if (!forwardWindow.Forward?.createPaymentElement) {
          setFieldError('Payment SDK is not available. Please refresh the page.')
          setLoading(false)
          return
        }

        let expMonth = ''
        let expYear = ''
        let lastFour = ''
        let cardType = ''

        // hidePostalCode / hideCountry belong to mount(), not here — see the
        // mount call below and the two ForwardBillingMethodCapture components,
        // which are the working references for this SDK.
        const paymentElement = await forwardWindow.Forward.createPaymentElement({
          apiKey: intent.publicApiKey,
          clientSecret: intent.clientSecret,
        })

        if (cancelled) return

        mountedRef.current = true
        paymentElementRef.current = paymentElement
        setLoading(false)

        paymentElement.mount(containerRef.current!, {
          hidePostalCode: true,
          hideCountry: true,
          onChange: (event: { ready?: boolean; card?: { expMonth?: string; expYear?: string; brand?: string; lastFourDigits?: string } }) => {
            if (event.ready && event.card) {
              expMonth = event.card.expMonth ?? ''
              expYear = event.card.expYear ?? ''
              lastFour = event.card.lastFourDigits ?? ''
              cardType = event.card.brand ?? ''
            }
          },
          onLinkedDefaultAccount: (event: { payment_method_type?: string; provider_name?: string; account_number?: string }) => {
            if (event.payment_method_type === 'bank') {
              const acct = event.account_number ?? ''
              const title = `${event.provider_name ?? 'Bank'} XXXX${acct.slice(-4)}`
              expYear = '2099'
              expMonth = '01'
              cardType = 'bank'
              lastFour = acct.slice(-4)
              void title
            }
          },
          onReady: async (tokenize: () => Promise<void>) => {
            await tokenize()
          },
          onSuccess: ({ token }: { token: string }) => {
            if (cancelled) return
            const paddedMonth = expMonth.padStart(2, '0')
            const fullYear = expYear.length === 2 ? `20${expYear}` : expYear
            const normalizedType = cardType
              .split('_')
              .filter(Boolean)
              .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
              .join(' ')
            const title = `${normalizedType} XXXX${lastFour} (${paddedMonth}/${fullYear.slice(-2)})`
            const expireDate = `${fullYear}-${paddedMonth}-01`
            handleSuccess(token, title, expireDate, cardType)
          },
          onError: (err: unknown) => {
            if (!cancelled) {
              const msg = err instanceof Error ? err.message : 'Card entry failed'
              setFieldError(msg)
            }
          },
        })
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Unable to load payment form'
          setFieldError(msg)
          setLoading(false)
        }
      }
    }

    init()

    return () => {
      cancelled = true
      const el = paymentElementRef.current
      if (el?.destroy) el.destroy()
      else if (el?.unmount) el.unmount()
      paymentElementRef.current = null
      mountedRef.current = false
      if (containerRef.current) containerRef.current.innerHTML = ''
    }
  }, [eformToken, merchantId, handleSuccess])

  if (!eformToken || merchantId <= 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center">
        <CreditCard className="h-8 w-8 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-amber-700 font-medium">Payment processing unavailable</p>
        <p className="text-xs text-amber-600 mt-1">
          This form's payment configuration is incomplete.
        </p>
      </div>
    )
  }

  if (cardAdded) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-4 text-center">
        <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
        <p className="text-sm text-green-700 font-medium">Billing method added successfully</p>
        {cardTitle && <p className="text-xs text-green-600 mt-1">{cardTitle}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {loading && (
        <div className="flex items-center justify-center py-4 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Loading secure payment form...</span>
        </div>
      )}
      {fieldError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {fieldError}
        </div>
      )}
      <div
        ref={containerRef}
        className="min-h-[140px] rounded-lg border border-slate-200 bg-white p-3 [&_[data-field=postalCode]]:hidden [&_[data-field=country]]:hidden"
        style={{ display: loading ? 'none' : 'block' }}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}
